/**
 * OrganizerAccessCard — owner-side control to issue/rotate the organizer desk
 * link + PIN for an event. Works for tournaments AND open play sessions:
 * pass apiBase = `/sports/tournaments/${id}` or `/sports/open-play/${id}`.
 */
import { useEffect, useState } from 'react';
import { Copy, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { sportsApi } from '../SportsAuthContext';

export default function OrganizerAccessCard({ apiBase }) {
  const [access, setAccess] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPin, setShowPin] = useState(false);

  useEffect(() => {
    setAccess(null);
    sportsApi.get(`${apiBase}/organizer-access`)
      .then(r => setAccess(r.data)).catch(() => {});
  }, [apiBase]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await sportsApi.post(`${apiBase}/organizer-access`, {});
      setAccess({ exists: true, path: r.data.path, pin: r.data.pin });
      setShowPin(true);
      toast.success(access?.exists ? 'Organizer link rotated — old link is dead' : 'Organizer link created');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate organizer access');
    } finally { setBusy(false); }
  };

  const fullUrl = access?.path ? `${window.location.origin}${access.path}` : '';
  return (
    <div className="rounded-2xl p-5 mb-6" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A' }} data-testid="organizer-access-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-bold" style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase' }}>Organizer Desk Access</p>
          <p className="text-xs mt-0.5" style={{ color: '#737373' }}>
            Private link + PIN for the event organizer's crew — they collect entry fees into this
            event's own cashbox without an account or access to your system. Every peso is logged
            under the marshal's name.
          </p>
        </div>
        <button onClick={generate} disabled={busy} data-testid="organizer-access-generate"
          className="px-3 py-2 rounded-lg text-xs font-bold uppercase"
          style={{ background: '#E6394618', color: '#E63946', border: '1px solid #E6394640', cursor: 'pointer' }}>
          {busy ? 'Working…' : access?.exists ? 'Rotate link + PIN' : 'Generate link + PIN'}
        </button>
      </div>
      {access?.exists && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <code className="text-xs px-2 py-1.5 rounded" style={{ background: '#0A0A0A', color: '#A3A3A3', border: '1px solid #1E1E1E', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fullUrl}</code>
          <button onClick={() => { navigator.clipboard.writeText(fullUrl); toast.success('Organizer link copied'); }}
            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold" style={{ background: '#1E1E1E', color: '#A3A3A3', cursor: 'pointer' }}>
            <Copy className="h-3 w-3 inline mr-1" />Copy
          </button>
          <span className="text-xs px-2.5 py-1.5 rounded-lg font-bold" style={{ background: '#0A0A0A', border: '1px solid #1E1E1E', color: '#FFB300', letterSpacing: 2 }}>
            PIN: {showPin ? access.pin : '••••••'}
          </span>
          <button onClick={() => setShowPin(v => !v)} className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1E1E1E', color: '#737373', cursor: 'pointer' }}>
            {showPin ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
      )}
    </div>
  );
}
